import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Phone, Mail, CheckCircle } from 'lucide-react';
import aboutGames from '../assets/about-games.png';
import aboutCompanionship from '../assets/about-companionship.png';
import aboutUnpacking from '../assets/about-unpacking.png';

const heroImages = [
  { src: aboutCompanionship, alt: 'Care worker and elderly client in companionship visit' },
  { src: aboutGames, alt: 'Care worker and elderly client playing board games' },
  { src: aboutUnpacking, alt: 'Care worker helping elderly client unpack shopping' },
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const total = heroImages.length;

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden w-full">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-50 via-teal-50 to-emerald-50 opacity-60"></div>
      <div className="absolute top-20 right-0 w-64 h-64 lg:w-96 lg:h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 lg:w-96 lg:h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          <div className="space-y-8">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-medium">
                Trusted Senior Care Support
              </span>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              Reliable Home Support to Help Your Loved Ones Stay{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-teal-600">
                Safe and Independent
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
              Providing companionship and practical home support across Liverpool and surrounding areas.
            </p>

            <div className="flex flex-wrap gap-3">
              {['Fully Insured', 'DBS Checked', 'Local & Reliable'].map((item) => (
                <div key={item} className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <span className="text-slate-700 font-medium text-sm sm:text-base">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-base sm:text-lg text-slate-600 font-medium">
              Call today to arrange support or discuss your needs
            </p>

            <div className="space-y-3">
              <a href="tel:07475473449" className="flex items-center space-x-3 text-teal-600 hover:text-teal-700 transition-colors group">
                <div className="w-10 h-10 bg-teal-600 group-hover:bg-teal-700 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl sm:text-2xl font-bold">07475 473 449</span>
              </a>
              <a href="mailto:enquiries@aftseniorassistedservices.co.uk" className="flex items-center space-x-3 text-slate-600 hover:text-teal-600 transition-colors group">
                <div className="w-10 h-10 bg-slate-200 group-hover:bg-teal-100 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                  <Mail className="w-5 h-5 text-slate-600 group-hover:text-teal-600" />
                </div>
                <span className="text-sm break-all">enquiries@aftseniorassistedservices.co.uk</span>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:07475473449" className="w-full sm:w-auto">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 transition-colors w-full">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Today
                </Button>
              </a>
              <Button onClick={scrollToContact} size="lg" variant="outline" className="border-2 border-teal-600 text-teal-700 hover:bg-teal-50 text-lg px-8 py-6 transition-colors w-full sm:w-auto">
                Get in Touch
              </Button>
            </div>
          </div>

          <div className="relative w-full">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[300px] sm:h-[400px] md:h-[500px] lg:h-[560px]">
              {heroImages.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000 ${index === current ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 to-transparent"></div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;